import { ReactNode } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/iconify';

// Shared dark "media" dialog used for live video, live photo, and recorded
// event clips. Dark surface so footage pops, compact header with the vehicle +
// an optional LIVE indicator, subtle close button, and an optional footer strip.

const CAMERA_DIALOG_BG = '#0E1116';

export function CameraMediaDialog({
  open,
  onClose,
  title,
  subtitle,
  live = false,
  downloadUrl,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  live?: boolean;
  downloadUrl?: string;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      transitionDuration={140}
      PaperProps={{
        sx: {
          bgcolor: CAMERA_DIALOG_BG,
          borderRadius: 2.5,
          overflow: 'hidden',
          boxShadow: '0 24px 60px -12px rgba(0,0,0,0.6)',
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, py: 1.75 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 1.5,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha('#fff', 0.08),
          }}
        >
          <Iconify icon="solar:camera-bold" width={20} sx={{ color: '#fff' }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.25 }} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.55) }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        {live && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: alpha('#FF3B30', 0.16), flexShrink: 0 }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: '#FF453A',
                '@keyframes camPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
                animation: 'camPulse 1.3s ease-in-out infinite',
              }}
            />
            <Typography variant="caption" sx={{ color: '#FF6B60', fontWeight: 800, letterSpacing: 0.6 }}>
              LIVE
            </Typography>
          </Stack>
        )}
        {downloadUrl && (
          <IconButton
            component="a"
            href={downloadUrl}
            target="_blank"
            rel="noopener"
            title="Download video"
            sx={{
              color: alpha('#fff', 0.7),
              flexShrink: 0,
              '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) },
            }}
          >
            <Iconify icon="solar:download-minimalistic-bold" width={20} />
          </IconButton>
        )}
        <IconButton
          onClick={onClose}
          sx={{
            color: alpha('#fff', 0.7),
            flexShrink: 0,
            '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) },
          }}
        >
          <Iconify icon="mingcute:close-line" width={20} />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2.5, pb: footer ? 1.5 : 2.5 }}>
        <Box
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#000',
            minHeight: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </Box>
      </Box>

      {footer && <Box sx={{ px: 2.5, pb: 2.5 }}>{footer}</Box>}
    </Dialog>
  );
}

export function CameraMediaLoading({ label, sublabel }: { label: string; sublabel?: string }) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 7, px: 3, textAlign: 'center' }}>
      <CircularProgress size={40} sx={{ color: alpha('#fff', 0.9) }} />
      <Typography sx={{ color: '#fff', fontWeight: 600 }}>{label}</Typography>
      {sublabel && (
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
          {sublabel}
        </Typography>
      )}
    </Stack>
  );
}

export function CameraMediaMessage({
  icon,
  iconColor,
  title,
  subtitle,
  actionLabel,
  actionIcon,
  onAction,
}: {
  icon: string;
  iconColor?: string;
  title: string;
  subtitle?: string;
  actionLabel?: string;
  actionIcon?: string;
  onAction?: () => void;
}) {
  return (
    <Stack alignItems="center" spacing={2} sx={{ py: 6, px: 3, textAlign: 'center' }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha('#fff', 0.08),
        }}
      >
        <Iconify icon={icon} width={28} sx={{ color: iconColor || alpha('#fff', 0.6) }} />
      </Box>
      <Box>
        <Typography sx={{ color: '#fff', fontWeight: 700 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.55) }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actionLabel && onAction && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAction}
          startIcon={actionIcon ? <Iconify icon={actionIcon} /> : undefined}
        >
          {actionLabel}
        </Button>
      )}
    </Stack>
  );
}
